import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useState, useMemo, useCallback } from "react";
import { Plus, CalendarIcon, Trash2, ClipboardCopy, Loader2, SlidersHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useOrganization } from "@/hooks/useOrganization";
import { useOrgHall } from "@/hooks/useOrgHall";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PricingCategoryTabs, { type PricingCategory } from "./pricing/PricingCategoryTabs";
import PricingRuleList from "./pricing/PricingRuleList";
import PricingWeekGrid from "./pricing/PricingWeekGrid";
import PricingRuleDialog, { type PricingFormData } from "./pricing/PricingRuleDialog";
import { useCustomerCategories } from "@/hooks/useCustomerCategories";
import type { Tables } from "@/integrations/supabase/types";

type PricingRule = Tables<"pricing_rules">;

interface SpecialPriceDate {
  id: string;
  organization_id: string;
  date: string;
  label: string | null;
  price_list_type: string;
}

const PRICE_LIST_VALUES = ["standard", "holiday", "special"] as const;

type PriceListType = typeof PRICE_LIST_VALUES[number];

export default function AdminPricing() {
  const { t } = useTranslation("admin");

  const PRICE_LIST_TABS = [
    { value: "standard" as const, label: t("pricing.standard") },
    { value: "holiday" as const, label: t("pricing.holidays") },
    { value: "special" as const, label: t("pricing.specialPrices") },
  ];

  const qc = useQueryClient();
  const { organization } = useOrganization();
  const { hallId, hallHours, hall } = useOrgHall();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [dialogInitial, setDialogInitial] = useState<Partial<PricingFormData> | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("standard");
  const [priceListType, setPriceListType] = useState<PriceListType>("standard");

  // Bulk adjust
  const [bulkAdjustOpen, setBulkAdjustOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState<"kr" | "percent">("kr");
  const [bulkAmount, setBulkAmount] = useState<number>(0);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  // Special dates
  const [newDateLabel, setNewDateLabel] = useState("");
  const [newDate, setNewDate] = useState<Date | undefined>();
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  const { categories: dbCategories, addCategory, renameCategory, deleteCategory, updateCategoryRules, getCategoryDependencies } =
    useCustomerCategories(organization?.id);

  const legacyCategories: PricingCategory[] = useMemo(
    () => dbCategories.map((c) => ({ key: c.slug, label: c.name })),
    [dbCategories]
  );

  const { data: rules = [] } = useQuery({
    queryKey: ["admin-pricing", hallId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pricing_rules")
        .select("*")
        .eq("hall_id", hallId!)
        .order("start_time");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hallId,
  });

  const { data: specialDates = [] } = useQuery({
    queryKey: ["special-price-dates", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("special_price_dates" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("date");
      if (error) throw error;
      return (data ?? []) as SpecialPriceDate[];
    },
    enabled: !!organization?.id,
  });

  const filteredRules = useMemo(
    () => rules.filter((r) =>
      r.category === selectedCategory &&
      (r.price_list_type ?? "standard") === priceListType
    ),
    [rules, selectedCategory, priceListType]
  );

  // Categories that have rules for the current price list type
  const categoriesWithRules = useMemo(() => {
    const cats = new Set<string>();
    rules.forEach((r) => {
      if ((r.price_list_type ?? "standard") === priceListType) {
        cats.add(r.category ?? "standard");
      }
    });
    return cats;
  }, [rules, priceListType]);

  // For copy-from: available source categories (those with rules, excluding current)
  const copySourceCategories = useMemo(() => {
    return Array.from(categoriesWithRules)
      .filter((c) => c !== selectedCategory)
      .map((slug) => {
        const cat = dbCategories.find((dc) => dc.slug === slug);
        return { slug, label: cat?.name ?? slug };
      });
  }, [categoriesWithRules, selectedCategory, dbCategories]);

  const [copySource, setCopySource] = useState("standard");
  const _showCopyOffer = filteredRules.length === 0 && copySourceCategories.length > 0 && selectedCategory !== "standard";
  // Also show for standard category if it's empty but other cats have rules
  const showCopyOfferAny = filteredRules.length === 0 && copySourceCategories.length > 0;

  const copyMutation = useMutation({
    mutationFn: async (sourceCategory: string) => {
      if (!hallId) throw new Error(t("pricing.noHall"));
      const sourceRules = rules.filter((r) =>
        r.category === sourceCategory &&
        (r.price_list_type ?? "standard") === priceListType
      );
      if (sourceRules.length === 0) throw new Error(t("pricing.noRulesToCopy"));
      const inserts = sourceRules.map((r) => ({
        name: r.name,
        start_time: r.start_time,
        end_time: r.end_time,
        price_per_hour: r.price_per_hour,
        is_peak: r.is_peak,
        day_of_week: r.day_of_week,
        color: r.color,
        category: selectedCategory,
        hall_id: hallId,
        price_list_type: priceListType,
      }));
      const { error } = await supabase.from("pricing_rules").insert(inserts);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-pricing", hallId] });
      toast(t("pricing.rulesCopied"), {
        description: t("pricing.adjustAllPricesQuestion"),
        action: {
          label: t("pricing.adjustAll"),
          onClick: () => { setBulkAmount(0); setBulkMode("kr"); setBulkAdjustOpen(true); },
        },
        duration: 8000,
      });
    },
    onError: (e: Error) => toast.error(e.message || t("pricing.couldNotCopy")),
  });

  const filteredSpecialDates = useMemo(
    () => specialDates.filter((sd) => sd.price_list_type === priceListType),
    [specialDates, priceListType]
  );

  const saveMutation = useMutation({
    mutationFn: async (data: PricingFormData & { id?: string }) => {
      if (!hallId) throw new Error(t("pricing.noHall"));
      const payload = {
        name: data.name,
        start_time: data.start_time,
        end_time: data.end_time,
        price_per_hour: data.price_per_hour,
        is_peak: data.is_peak,
        day_of_week: data.day_of_week,
        color: data.color,
        category: data.category,
        hall_id: hallId,
        price_list_type: priceListType,
      };
      if (data.id) {
        const { error } = await supabase.from("pricing_rules").update(payload).eq("id", data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("pricing_rules").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-pricing", hallId] });
      setDialogOpen(false);
      setEditId(null);
      setDialogInitial(null);
      toast.success(t("admin:toast.priceSaved"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("pricing.saveError")); },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pricing_rules").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-pricing", hallId] });
      toast.success(t("admin:toast.priceRemoved"));
    },
  });

  const addSpecialDateMutation = useMutation({
    mutationFn: async ({ date, label, type }: { date: Date; label: string; type: string }) => {
      const dateStr = format(date, "yyyy-MM-dd");
      const { error } = await supabase
        .from("special_price_dates" as any)
        .insert({
          organization_id: organization!.id,
          date: dateStr,
          label: label || null,
          price_list_type: type,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["special-price-dates"] });
      setNewDate(undefined);
      setNewDateLabel("");
      toast.success(t("admin:toast.dateAdded"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("pricing.couldNotAddDate")); },
  });

  const removeSpecialDateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("special_price_dates" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["special-price-dates"] });
      toast.success(t("admin:toast.dateRemoved"));
    },
  });

  const handleResize = useCallback((ruleId: string, newStart: string, newEnd: string) => {
    saveMutation.mutate({
      ...(rules.find((r) => r.id === ruleId)!),
      id: ruleId,
      start_time: newStart,
      end_time: newEnd,
    });
  }, [rules, saveMutation]);

  const handleInlineUpdate = useCallback((ruleId: string, data: { price_per_hour: number; is_peak: boolean; start_time: string; end_time: string; name: string; day_of_week: number[] }) => {
    const existing = rules.find((r) => r.id === ruleId);
    if (!existing) return;
    saveMutation.mutate({
      ...existing,
      id: ruleId,
      ...data,
      color: data.is_peak ? "#f59e0b" : "#10b981",
    });
  }, [rules, saveMutation]);

  const handleInlineDelete = useCallback((ruleId: string) => {
    deleteMutation.mutate(ruleId);
  }, [deleteMutation]);

  const handleMoveRule = useCallback((ruleId: string, data: { day_of_week: number[]; start_time: string; end_time: string }) => {
    const existing = rules.find((r) => r.id === ruleId);
    if (!existing) return;
    saveMutation.mutate({
      ...existing,
      id: ruleId,
      ...data,
    });
  }, [rules, saveMutation]);

  const handleMergeRules = useCallback((ruleIds: string[], keepRuleId: string) => {
    const selected = rules.filter((r) => ruleIds.includes(r.id));
    if (selected.length < 2) return;
    // Find earliest start and latest end
    let minStart = "23:59";
    let maxEnd = "00:00";
    for (const r of selected) {
      if (r.start_time?.slice(0, 5) < minStart) minStart = r.start_time.slice(0, 5);
      if (r.end_time?.slice(0, 5) > maxEnd) maxEnd = r.end_time.slice(0, 5);
    }
    const keepRule = selected.find((r) => r.id === keepRuleId) || selected[0];
    // Update the kept rule with merged times
    saveMutation.mutate({
      ...keepRule,
      id: keepRuleId,
      start_time: minStart,
      end_time: maxEnd,
    });
    // Delete the others
    for (const id of ruleIds) {
      if (id !== keepRuleId) deleteMutation.mutate(id);
    }
    toast.success(t("admin:toast.blocksMerged"));
  }, [rules, saveMutation, deleteMutation]);

  const clearMutation = useMutation({
    mutationFn: async () => {
      const ids = filteredRules.map((r) => r.id);
      if (ids.length === 0) return 0;
      const { error } = await supabase
        .from("pricing_rules")
        .delete()
        .in("id", ids);
      if (error) throw error;
      return ids.length;
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ["admin-pricing", hallId] });
      setClearConfirmOpen(false);
      toast.success(t("pricing.rulesRemoved", { count }));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("pricing.couldNotClearRules")); },
  });

  const bulkAdjustMutation = useMutation({
    mutationFn: async () => {
      if (!hallId || bulkAmount === 0) return;
      const updates = filteredRules.map((r) => {
        const newPrice = bulkMode === "kr"
          ? Math.max(0, r.price_per_hour + bulkAmount)
          : Math.max(0, Math.round(r.price_per_hour * (1 + bulkAmount / 100)));
        return { id: r.id, price_per_hour: newPrice };
      });
      for (const u of updates) {
        const { error } = await supabase.from("pricing_rules").update({ price_per_hour: u.price_per_hour }).eq("id", u.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-pricing", hallId] });
      setBulkAdjustOpen(false);
      toast.success(t("admin:toast.pricesAdjusted"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("pricing.adjustmentError")); },
  });

  const openEdit = (rule: PricingRule) => {
    setEditId(rule.id);
    setDialogInitial({
      name: rule.name,
      start_time: rule.start_time?.slice(0, 5),
      end_time: rule.end_time?.slice(0, 5),
      price_per_hour: rule.price_per_hour,
      is_peak: rule.is_peak,
      day_of_week: rule.day_of_week ?? [],
      color: rule.color ?? "#3b82f6",
      category: rule.category ?? "standard",
    });
    setDialogOpen(true);
  };

  const openNew = (defaults?: Partial<PricingFormData>) => {
    setEditId(null);
    setDialogInitial({ category: selectedCategory, ...defaults });
    setDialogOpen(true);
  };

  const handleSave = (data: PricingFormData) => {
    saveMutation.mutate({ ...data, id: editId ?? undefined });
  };

  const hours = hallHours ?? { start: 6, end: 24 };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-sm font-semibold">{t("pricing.pricingRules")}{hall?.name ? ` – ${hall.name}` : ""}</h2>
        <Button size="sm" onClick={() => openNew()}>
          <Plus className="h-4 w-4 mr-1" />{t("pricing.newRule")}
        </Button>
      </div>

      {/* Price list type tabs */}
      <Tabs value={priceListType} onValueChange={(v) => setPriceListType(v as PriceListType)}>
        <TabsList>
          {PRICE_LIST_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.value === "holiday" && "🔴 "}
              {t.value === "special" && "⭐ "}
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {PRICE_LIST_TABS.map((tab) => (
          <TabsContent key={tab.value} value={tab.value} className="space-y-4">
            {/* Date management for holiday / special */}
            {tab.value !== "standard" && (
              <div className="rounded-lg border p-4 space-y-3">
                <h3 className="text-sm font-medium">
                  {tab.value === "holiday" ? t("pricing.holidayDates") : t("pricing.specialDates")}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t("pricing.addDatesHint", { type: tab.value === "holiday" ? t("pricing.holiday") : t("pricing.special") })}
                </p>

                {/* Add new date */}
                <div className="flex items-end gap-2">
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">{t("pricing.date")}</span>
                    <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className={cn("w-[180px] justify-start text-left", !newDate && "text-muted-foreground")}>
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newDate ? format(newDate, "d MMM yyyy", { locale: sv }) : t("pricing.selectDate")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={newDate}
                          onSelect={(d) => { setNewDate(d); setDatePopoverOpen(false); }}
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-1 flex-1">
                    <span className="text-xs text-muted-foreground">{t("pricing.labelOptional")}</span>
                    <Input
                      placeholder={tab.value === "holiday" ? t("pricing.exHoliday") : t("pricing.exSpecial")}
                      value={newDateLabel}
                      onChange={(e) => setNewDateLabel(e.target.value)}
                      className="h-9"
                    />
                  </div>
                  <Button
                    size="sm"
                    disabled={!newDate || addSpecialDateMutation.isPending}
                    onClick={() => newDate && addSpecialDateMutation.mutate({ date: newDate, label: newDateLabel, type: tab.value })}
                  >
                    <Plus className="h-4 w-4 mr-1" /> {t("common.add")}
                  </Button>
                </div>

                {/* Existing dates */}
                {filteredSpecialDates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {filteredSpecialDates.map((sd) => (
                      <Badge key={sd.id} variant="secondary" className="gap-1 pr-1">
                        {format(new Date(sd.date + "T00:00:00"), "d MMM", { locale: sv })}
                        {sd.label && ` — ${sd.label}`}
                        <button
                          onClick={() => removeSpecialDateMutation.mutate(sd.id)}
                          className="ml-1 hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Category tabs */}
            <PricingCategoryTabs
              dbCategories={dbCategories}
              selected={selectedCategory}
              onSelect={setSelectedCategory}
              onAdd={(name) => organization && addCategory.mutate({ name, orgId: organization.id })}
              onRename={(cat, newName) => renameCategory.mutate({ id: cat.id, oldSlug: cat.slug, newName, hallId })}
              onRemove={(cat, moveTo) => deleteCategory.mutate({ id: cat.id, moveTo })}
              onGetDependencies={getCategoryDependencies}
              onUpdateDailyLimit={(cat, limit, fallback, overflow) => {
                updateCategoryRules.mutate({
                  id: cat.id,
                  daily_minutes_limit: limit,
                  daily_limit_fallback_category: fallback,
                  overflow_percent: overflow,
                });
              }}
              editable
            />

            {/* Empty state with copy offer */}
            {showCopyOfferAny && (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-10 text-center space-y-3">
                  <span className="text-4xl">📋</span>
                  <h3 className="text-sm font-semibold">
                    {t("pricing.noRulesFor", { category: dbCategories.find((c) => c.slug === selectedCategory)?.name ?? selectedCategory })}
                    {priceListType !== "standard" && ` (${PRICE_LIST_TABS.find((t) => t.value === priceListType)?.label})`}
                  </h3>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    {t("pricing.copyOffer")}
                  </p>
                  <div className="flex items-center gap-2">
                    {copySourceCategories.length === 1 ? (
                      <Button
                        onClick={() => copyMutation.mutate(copySourceCategories[0].slug)}
                        disabled={copyMutation.isPending}
                        className="gap-2"
                      >
                        {copyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCopy className="h-4 w-4" />}
                        {t("pricing.copyFromCategory", { category: copySourceCategories[0].label })}
                      </Button>
                    ) : (
                      <>
                        <Select value={copySource} onValueChange={setCopySource}>
                          <SelectTrigger className="h-9 w-44 text-sm">
                            <SelectValue placeholder={t("pricing.copyFrom")} />
                          </SelectTrigger>
                          <SelectContent>
                            {copySourceCategories.map((c) => (
                              <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={() => copyMutation.mutate(copySource)}
                          disabled={copyMutation.isPending}
                          className="gap-2"
                        >
                          {copyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCopy className="h-4 w-4" />}
                          {t("pricing.copy")}
                        </Button>
                      </>
                    )}
                  </div>
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                    onClick={() => openNew()}
                  >
                    {t("pricing.orFromScratch")}
                  </button>
                </CardContent>
              </Card>
            )}

            {/* View tabs — only show when there are rules or no copy offer */}
            {(!showCopyOfferAny || filteredRules.length > 0) && (
              <Tabs defaultValue="grid">
                <div className="flex items-center justify-between">
                  <TabsList>
                    <TabsTrigger value="grid">{ t("pricing.weekSchedule") }</TabsTrigger>
                    <TabsTrigger value="list">{ t("pricing.list") }</TabsTrigger>
                  </TabsList>

                  {filteredRules.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{filteredRules.length} { t("pricing.rules") }</span>
                      <Button size="sm" variant="outline" onClick={() => { setBulkAmount(0); setBulkMode("kr"); setBulkAdjustOpen(true); }}>
                        <SlidersHorizontal className="h-4 w-4 mr-1" />{t("pricing.adjustAll")}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setClearConfirmOpen(true)}>
                        <Trash2 className="h-4 w-4 mr-1" />{t("pricing.clear")}
                      </Button>
                    </div>
                  )}
                </div>

                <TabsContent value="grid">
                  <PricingWeekGrid
                    rules={filteredRules}
                    hallHours={hours}
                    timeStepMinutes={hall?.time_step_minutes ?? 30}
                    onClickRule={openEdit}
                    onClickEmpty={(day, time) => openNew({ day_of_week: [day], start_time: time, end_time: time < "23:00" ? `${String(parseInt(time) + 1).padStart(2, "0")}:00` : "23:30" })}
                    onResizeRule={handleResize}
                    onMoveRule={handleMoveRule}
                    onMergeRules={handleMergeRules}
                    onUpdateRule={handleInlineUpdate}
                    onDeleteRule={handleInlineDelete}
                    onQuickCreate={(data) => {
                      saveMutation.mutate({
                        ...data,
                        color: data.is_peak ? "#f59e0b" : "#10b981",
                        category: selectedCategory,
                      });
                    }}
                  />
                </TabsContent>

                <TabsContent value="list">
                  <PricingRuleList
                    rules={filteredRules}
                    categories={legacyCategories}
                    onEdit={openEdit}
                    onDelete={(id) => deleteMutation.mutate(id)}
                  />
                </TabsContent>
              </Tabs>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <PricingRuleDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditId(null); setDialogInitial(null); } }}
        initial={dialogInitial}
        editId={editId}
        categories={legacyCategories}
        onSave={handleSave}
        saving={saveMutation.isPending}
      />

      {/* Bulk price adjustment dialog */}
      <Dialog open={bulkAdjustOpen} onOpenChange={setBulkAdjustOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              {t("pricing.adjustAllPrices")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm">{t("pricing.changeBy")}:</span>
              <ToggleGroup type="single" value={bulkMode} onValueChange={(v) => v && setBulkMode(v as "kr" | "percent")}>
                <ToggleGroupItem value="kr" className="text-xs h-8 px-3">kr</ToggleGroupItem>
                <ToggleGroupItem value="percent" className="text-xs h-8 px-3">%</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <NumberInput
              placeholder={bulkMode === "kr" ? t("pricing.adjustPlaceholderKr") : t("pricing.adjustPlaceholderPercent")}
              value={bulkAmount ?? ""}
              onValueChange={(v) => setBulkAmount(v)}
              className="h-9"
              autoFocus
            />
            {bulkAmount !== 0 && filteredRules.length > 0 && (
              <div className="rounded border p-3 space-y-1 max-h-48 overflow-y-auto">
                <p className="text-xs font-medium text-muted-foreground mb-2">{t("pricing.preview")}</p>
                {filteredRules.map((r) => {
                  const newPrice = bulkMode === "kr"
                    ? Math.max(0, r.price_per_hour + bulkAmount)
                    : Math.max(0, Math.round(r.price_per_hour * (1 + bulkAmount / 100)));
                  return (
                    <div key={r.id} className="flex justify-between text-sm">
                      <span className="truncate">{r.name}</span>
                      <span>
                        <span className="text-muted-foreground line-through mr-1">{r.price_per_hour}</span>
                        <span className="font-medium">{newPrice} kr</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkAdjustOpen(false)}>{ t("common.cancel") }</Button>
            <Button
              onClick={() => bulkAdjustMutation.mutate()}
              disabled={bulkAmount === 0 || bulkAdjustMutation.isPending}
            >
              {bulkAdjustMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {t("pricing.apply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear rules confirmation dialog */}
      <Dialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("pricing.clearRulesTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("pricing.clearConfirm", { count: filteredRules.length, category: dbCategories.find((c) => c.slug === selectedCategory)?.name ?? selectedCategory })}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setClearConfirmOpen(false)}>{ t("common.cancel") }</Button>
            <Button
              variant="destructive"
              onClick={() => clearMutation.mutate()}
              disabled={clearMutation.isPending}
            >
              {clearMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {t("pricing.yesClearAll")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
